import React, { useState, useMemo, useEffect } from 'react';
import { Upload, GraduationCap, ChevronDown, Target, Sparkles, AlertTriangle, X, Trash2, Loader2 } from 'lucide-react';
import { useUser } from "../../../context/UserContext";
import { useSequence } from "../../../hooks/fetch_api_hook/useSequence";
import { useTheme } from "../../../hooks/fetch_api_hook/useTheme";
import useUpdateGroupe from "../../../hooks/update_api_hook/useUpdateGroupe";
import { useDeleteSequence } from "../../../hooks/delete_api_hook/useDeleteSequence";
import { url } from "../../../utils/confserveur";
import ImportProgressionNiveau from './ImportProgressionNiveau';
import ButtonAddSequenceNiveau from './ButtonAddSequenceNiveau';
import AddSequenceNiveau from './AddSequenceNiveau';
import EditSequenceNiveau from './EditSequenceNiveau';
import AddSavoirToSequence from './AddSavoirToSequence';
import AddNiveauActivityProgrammation from './AddNiveauActivityProgrammation';
import ActivityListModal from './ActivityListModal';
import SequenceNode from './SequenceNode';
import VacancesSeparator from './VacancesSeparator';
import RailAutomatismes from './RailAutomatismes';
import BandeauAutomatismes from './BandeauAutomatismes';
import PanneauContenus from './PanneauContenus';
import PanneauNonAbordes from './PanneauNonAbordes';
import { fetchSchoolHolidays } from "../../../utils/vacances";
import { partitionnerParRubrique } from "../../../utils/rubriqueAttendus";
import { usePostApi } from "../../../hooks/post_api_hook/usePostApi";

// Modal de confirmation de suppression
const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, sequence, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-md overflow-hidden">
        {/* Header orange */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500" />

          {/* Animated background pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
            <path fill="white">
              <animate attributeName="d" dur="4s" repeatCount="indefinite"
                values="M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,80 200,50 Q300,20 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z" />
            </path>
          </svg>

          <div className="relative px-3 sm:px-6 py-3 sm:py-5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-white truncate">
                  Confirmer la suppression
                </h2>
                <p className="text-white/80 text-xs sm:text-sm">
                  Cette action est irréversible
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-6">
          <div className="mb-4 sm:mb-6">
            <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
              Êtes-vous sûr de vouloir supprimer cette {sequence?.type === 'Séquence' ? 'séquence' : `activité (${sequence?.type})`}  :
            </p>
            <div className="p-3 sm:p-4 bg-orange-50 border-2 border-orange-200 rounded-lg sm:rounded-xl">
              <p className="font-bold text-orange-800 text-sm sm:text-lg">
                {sequence?.title}
              </p>
              <p className="text-xs sm:text-sm text-orange-600 mt-1">
                {sequence?.theme_for_sequence}
              </p>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">
              Tous les savoirs-faires et attendus associés seront également supprimés de cette {sequence?.type === 'Séquence' ? 'séquence' : `activité (${sequence?.type})`}.
            </p>
          </div>

          {/* Boutons */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm sm:text-base"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg sm:rounded-xl font-medium
                hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/30
                hover:shadow-xl hover:shadow-orange-500/40 disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm sm:text-base">Suppression...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Supprimer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant principal RoadMap
function RoadMapNiveau({ etablissement }) {
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [reloadSequence, setReloadSequence] = useState(false);

  // State pour le modal d'ajout de savoirs
  const [isAddSavoirModalOpen, setIsAddSavoirModalOpen] = useState(false);
  const [selectedSequenceForSavoir, setSelectedSequenceForSavoir] = useState(null);

  // State pour le modal d'édition de séquence
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSequenceForEdit, setSelectedSequenceForEdit] = useState(null);

  // State pour le modal de création de séquence
  const [isAddSequenceModalOpen, setIsAddSequenceModalOpen] = useState(false);

  // State pour le modal de confirmation de suppression
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSequenceForDelete, setSelectedSequenceForDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State pour le modal d'ajout d'activité en parallèle
  const [isAddActivityModalOpen, setIsAddActivityModalOpen] = useState(false);
  const [selectedSequenceForActivity, setSelectedSequenceForActivity] = useState(null);

  // State pour le modal de liste des activités
  const [isActivityListModalOpen, setIsActivityListModalOpen] = useState(false);
  const [activityListModalData, setActivityListModalData] = useState({ type: null, activities: [], sequenceTitle: '' });

  // States pour les loadings des modals
  const [isCreatingSequence, setIsCreatingSequence] = useState(false);
  const [isEditingSequence, setIsEditingSequence] = useState(false);
  const [isSavingSavoirs, setIsSavingSavoirs] = useState(false);
  const [isCreatingActivity, setIsCreatingActivity] = useState(false);

  // Hook pour mise à jour des séquences
  const { updateGroupe } = useUpdateGroupe();

  // Hook pour suppression des séquences
  const { deleteSequence } = useDeleteSequence();

  // Hook pour création de séquences
  const [, doPost] = usePostApi();

  // Récupération des séquences
  const { data: listSequences, isLoading } = useSequence(
    `${url}sequences/?etablissement=${etablissement?.id}`,
    reloadSequence
  );

  // useApi démarre à isLoading=false (le FETCH_INIT part dans un effet, donc
  // après le 1er rendu) : sans ce suivi, « Aucune séquence » flasherait au
  // montage puis pendant tout le fetch. Passe à true dès le premier
  // chargement observé et n'en bouge plus.
  const [sequencesInitialisees, setSequencesInitialisees] = useState(false);
  useEffect(() => {
    if (isLoading) setSequencesInitialisees(true);
  }, [isLoading]);

  // Récupération des thèmes pour le niveau sélectionné
  const { data: themes } = useTheme(selectedNiveau[0]);

  const niveaux = [
    { value: '6eme', label: '6ème' },
    { value: '5eme', label: '5ème' },
    { value: '4eme', label: '4ème' },
    { value: '3eme', label: '3ème' },
  ];

  const handleNiveauSelect = (niveau) => {
    setSelectedNiveau(niveau.value);
    setIsDropdownOpen(false);
  };

  const handleReload = () => {
    setReloadSequence((prev) => !prev);
  };

  // Ouvrir le modal d'ajout de savoirs
  const handleOpenAddSavoir = (sequence) => {
    setSelectedSequenceForSavoir(sequence);
    setIsAddSavoirModalOpen(true);
  };

  // Fermer le modal d'ajout de savoirs
  const handleCloseAddSavoir = () => {
    setIsAddSavoirModalOpen(false);
    setSelectedSequenceForSavoir(null);
  };

  // Ouvrir le modal d'édition de séquence
  const handleOpenEditSequence = (sequence) => {
    setSelectedSequenceForEdit(sequence);
    setIsEditModalOpen(true);
  };

  // Fermer le modal d'édition
  const handleCloseEditSequence = () => {
    setIsEditModalOpen(false);
    setSelectedSequenceForEdit(null);
  };

  // Ouvrir le modal de création de séquence
  const handleOpenAddSequence = () => {
    setIsAddSequenceModalOpen(true);
  };

  // Fermer le modal de création de séquence
  const handleCloseAddSequence = () => {
    setIsAddSequenceModalOpen(false);
  };

  // Ouvrir le modal de confirmation de suppression
  const handleOpenDeleteSequence = (sequence) => {
    setSelectedSequenceForDelete(sequence);
    setIsDeleteModalOpen(true);
  };

  // Fermer le modal de confirmation de suppression
  const handleCloseDeleteSequence = () => {
    setIsDeleteModalOpen(false);
    setSelectedSequenceForDelete(null);
  };

  // Ouvrir le modal d'ajout d'activité en parallèle
  const handleOpenAddActivity = (sequence) => {
    setActivityTypeInitial(null);
    setSelectedSequenceForActivity(sequence);
    setIsAddActivityModalOpen(true);
  };

  // Ouvrir le modal en mode « rituel de la période » (depuis le rail)
  const handleOpenAddRituel = (periode) => {
    setActivityTypeInitial('AUTOMATISME');
    setSelectedSequenceForActivity({ start: periode.date_debut, end: periode.date_fin });
    setIsAddActivityModalOpen(true);
  };

  // Idem pour le rail de droite : résolution de problèmes de la période
  const handleOpenAddResolution = (periode) => {
    setActivityTypeInitial('RESOLUTION');
    setSelectedSequenceForActivity({ start: periode.date_debut, end: periode.date_fin });
    setIsAddActivityModalOpen(true);
  };

  // Fermer le modal d'ajout d'activité en parallèle
  const handleCloseAddActivity = () => {
    setIsAddActivityModalOpen(false);
    setSelectedSequenceForActivity(null);
  };

  // Ouvrir le modal de liste des activités
  const handleViewActivities = (type, activities, sequenceTitle) => {
    setActivityListModalData({ type, activities, sequenceTitle });
    setIsActivityListModalOpen(true);
  };

  // Fermer le modal de liste des activités
  const handleCloseActivityList = () => {
    setIsActivityListModalOpen(false);
    setActivityListModalData({ type: null, activities: [], sequenceTitle: '' });
  };

  // Supprimer une séquence
  const handleConfirmDeleteSequence = async () => {
    if (!selectedSequenceForDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteSequence(selectedSequenceForDelete.id);

      // Si le modal ActivityList est ouvert, mettre à jour sa liste directement
      if (isActivityListModalOpen) {
        setActivityListModalData(prev => ({
          ...prev,
          activities: prev.activities.filter(act => act.id !== selectedSequenceForDelete.id)
        }));
      }

      handleReload();
      handleCloseDeleteSequence();
    } catch (error) {
      console.error('Erreur lors de la suppression de la séquence:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Créer une nouvelle activité en parallèle
  const handleCreateActivity = async (data) => {
    setIsCreatingActivity(true);
    try {
      const result = await doPost({
        title: data.title,
        start: data.start,
        end: data.end,
        theme: data.theme,
        type: data.type,
        niveau: selectedNiveau,
        savoir_faires: data.savoir_faires,
        attendus: data.attendus,
        est_commune: true,
        etablissement: etablissement?.id
      }, `${url}sequences/`);

      if (result.success) {
        handleReload();
        handleCloseAddActivity();
      } else {
        console.error('Erreur lors de la création de l\'activité:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'activité:', error);
    } finally {
      setIsCreatingActivity(false);
    }
  };

  // Créer une nouvelle séquence
  const handleCreateSequence = async (data) => {
    setIsCreatingSequence(true);
    try {
      const result = await doPost({
        title: data.title,
        start: data.start,
        end: data.end,
        theme: data.theme,
        niveau: data.niveau,
        savoir_faires: data.savoir_faires,
        attendus: data.attendus,
        est_commune: data.est_commune,
        etablissement: etablissement?.id
      }, `${url}sequences/`);

      if (result.success) {
        handleReload();
        handleCloseAddSequence();
      } else {
        console.error('Erreur lors de la création de la séquence:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la création de la séquence:', error);
    } finally {
      setIsCreatingSequence(false);
    }
  };

  // Sauvegarder les modifications de la séquence
  const handleSaveEditSequence = async (data) => {
    if (!selectedSequenceForEdit?.id) return;

    setIsEditingSequence(true);
    try {
      await updateGroupe(`${url}sequences/${selectedSequenceForEdit.id}/`, {
        title: data.title,
        start: data.start,
        end: data.end,
        // `theme` propagé UNIQUEMENT s'il a été résolu par le modal : le
        // réintroduire ici en dur (theme: data.theme) renverrait un undefined
        // et annulerait la garde d'EditSequenceNiveau. PATCH : clé absente =
        // valeur inchangée en base (une séquence sans thème est invisible).
        ...(data.theme !== undefined ? { theme: data.theme } : {}),
        savoir_faires: data.savoir_faires,
        attendus: data.attendus
      });
      handleReload();
      handleCloseEditSequence();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la séquence:', error);
    } finally {
      setIsEditingSequence(false);
    }
  };

  // Trouver le thème correspondant à la séquence sélectionnée
  const getThemeForSequence = (sequence) => {
  
    if (!themes || !sequence) return null;
    return themes.find(t => t.nom === sequence.theme_for_sequence);
  };

  // Sauvegarder les savoirs/attendus modifiés
  const handleSaveSavoirs = async (data) => {
    if (!selectedSequenceForSavoir?.id) return;

    setIsSavingSavoirs(true);
    try {

   
      await updateGroupe(`${url}sequences/${selectedSequenceForSavoir.id}/`, {
        savoir_faires: data.savoir_faires_details.map(sf => sf.id),
        attendus: data.attendus_details.map(att => att.id)
      });
      // Après succès, recharger les séquences et fermer le modal
      handleReload();
      handleCloseAddSavoir();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la séquence:', error);
    } finally {
      setIsSavingSavoirs(false);
    }
  };

  // Filtrer les séquences en fonction du niveau sélectionné
  const filteredSequences = useMemo(() => {
    if (!listSequences || !selectedNiveau) return [];
    return listSequences.filter(
      (sequence) => sequence.niveau === selectedNiveau && sequence.est_commune
    );
  }, [listSequences, selectedNiveau]);

  // Filtrer uniquement les séquences (pas les automatismes, qflash, etc.)
  const sequences = useMemo(() => {
    if (!filteredSequences) return [];
    return filteredSequences
      .filter(seq => seq.type === 'SEQUENCE')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [filteredSequences]);

  // Vacances scolaires (API calendrier + repli statique Réunion) : servent
  // UNIQUEMENT aux séparateurs visuels de la frise. Échec du fetch → liste
  // vide → frise inchangée, rien d'autre n'en dépend. Flag `ignore` : pas
  // de setState après démontage (et StrictMode inoffensif).
  const [vacancesScolaires, setVacancesScolaires] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  useEffect(() => {
    let ignore = false;
    (async () => {
      const { vacances: vac, periode, erreur } = await fetchSchoolHolidays();
      if (ignore || erreur) return;
      if (Array.isArray(vac)) setVacancesScolaires(vac);
      if (Array.isArray(periode)) setPeriodes(periode);
    })();
    return () => { ignore = true; };
  }, []);

  // Le rail des rituels n'existe que si les périodes sont connues, et
  // SEULEMENT HORS FRANÇAIS (2026-08-25) : le type AUTOMATISME leur est
  // fermé et leur référentiel n'a pas de rubrique automatisme — un rail
  // vide avec un bouton créant des QFlash invisibles serait cassé. Le
  // français garde la frise historique (QFlash en badges de séquences).
  // Le jour où on leur ouvre la progression parallèle, le rail portera
  // leurs QFlash. Sans périodes : même repli intégral pour tous.
  const railActif = periodes.length > 0 && user?.matiere !== 'Français';

  // Panneau détail partagé (pill d'une séquence chargée / chip d'un rituel)
  const [contenusActifs, setContenusActifs] = useState(null);

  // Panneau des attendus non abordés
  const [nonAbordesOpen, setNonAbordesOpen] = useState(false);

  // Ajout d'un rituel DEPUIS LE RAIL : le modal d'activité s'ouvre
  // pré-rempli aux bornes de la PÉRIODE (pas d'une séquence) et sur le
  // type AUTOMATISME — disponible même quand la période n'a aucune
  // séquence (retour prof 2026-08-25).
  const [activityTypeInitial, setActivityTypeInitial] = useState(null);

  // Frise mixte, dérivée PENDANT le render : les séquences (index d'origine
  // préservé — SequenceNode s'en sert pour l'alternance gauche/droite) + un
  // séparateur quand des vacances s'intercalent entre deux séquences
  // consécutives — le cas exact où l'import coupe en « Partie 1/2 ». Une
  // vacance hors de la plage affichée (année précédente, après la dernière
  // séquence) ne s'intercale nulle part : jamais affichée, par construction.
  const timeline = useMemo(() => {
    const items = sequences.map((sequence, index) => ({ type: 'sequence', sequence, index }));
    if (vacancesScolaires.length === 0) return items;
    const result = [];
    items.forEach((item, i) => {
      result.push(item);
      const next = items[i + 1];
      if (!next) return;
      const finCourante = new Date(item.sequence.end);
      const debutSuivante = new Date(next.sequence.start);
      vacancesScolaires
        .filter((v) => {
          if (!v.title || !v.title.includes('Vacances')) return false; // pas les entrées « Rentrée »
          const debutVac = new Date(v.start);
          return debutVac >= finCourante && debutVac <= debutSuivante;
        })
        .forEach((v) => result.push({ type: 'vacances', title: v.title, start: v.start, end: v.end }));
    });
    return result;
  }, [sequences, vacancesScolaires]);

  // Récupérer les activités parallèles (automatismes, qflash, résolutions).
  // Une activité se rattache à la séquence dont l'intervalle contient sa date
  // de début ; les ORPHELINES (date hors de toute séquence, typiquement après
  // édition/suppression de leur séquence d'origine) étaient silencieusement
  // écartées de la frise : on les collecte pour le filet d'affichage.
  const { activitesParalleles, activitesOrphelines } = useMemo(() => {
    if (!filteredSequences) return { activitesParalleles: {}, activitesOrphelines: [] };
    const activites = {};
    const orphelines = [];

    filteredSequences
      // les AUTOMATISME et RESOLUTION sortent du rattachement par séquence
      // quand les rails par période sont actifs (ils y vivent, rail gauche
      // et rail droit) ; seuls les QFlash restent en badge de séquence
      .filter(seq => seq.type !== 'SEQUENCE' && !(railActif && (seq.type === 'AUTOMATISME' || seq.type === 'RESOLUTION')))
      .forEach(act => {
        // Trouver la séquence correspondante par date
        const matchingSeq = sequences.find(seq => {
          const actStart = new Date(act.start);
          const seqStart = new Date(seq.start);
          const seqEnd = new Date(seq.end);
          return actStart >= seqStart && actStart <= seqEnd;
        });

        if (matchingSeq) {
          if (!activites[matchingSeq.id]) {
            activites[matchingSeq.id] = [];
          }
          activites[matchingSeq.id].push(act);
        } else {
          orphelines.push(act);
        }
      });

    return { activitesParalleles: activites, activitesOrphelines: orphelines };
  }, [filteredSequences, sequences, railActif]);

  // ── Les rituels d'automatismes : la progression PARALLÈLE par période ──
  // (maquette validée prof 2026-08-25 : rail desktop / bandeau mobile,
  // chips-séances, panneau détail partagé)
  const rituels = useMemo(() => {
    if (!railActif || !filteredSequences) return [];
    return filteredSequences
      .filter((seq) => seq.type === 'AUTOMATISME')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [railActif, filteredSequences]);

  // ── Les résolutions de problèmes : second fil conducteur, rail DROIT ──
  const resolutions = useMemo(() => {
    if (!railActif || !filteredSequences) return [];
    return filteredSequences
      .filter((seq) => seq.type === 'RESOLUTION')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [railActif, filteredSequences]);

  // Sections de frise par période : séquences (index global préservé pour
  // l'alternance) + rituels + résolutions, rattachés par DATE — un élément
  // daté pendant des vacances rejoint la période suivante, les orphelins du
  // rattachement par séquence disparaissent. Les vacances de fin de période
  // s'accrochent à leur section. null → repli sur la frise historique.
  const sections = useMemo(() => {
    if (!railActif || sequences.length === 0) return null;
    const secs = periodes.map((p) => ({ periode: p, sequences: [], rituels: [], resolutions: [], vacancesApres: null }));
    const attacher = (dateStr) => {
      const d = new Date(dateStr);
      let idx = periodes.findIndex((p) => d >= new Date(p.date_debut) && d < new Date(p.date_fin));
      if (idx === -1) idx = periodes.findIndex((p) => new Date(p.date_debut) >= d);
      if (idx === -1) idx = periodes.length - 1;
      return idx;
    };
    sequences.forEach((sequence, index) => {
      secs[attacher(sequence.start)].sequences.push({ sequence, index });
    });
    rituels.forEach((r) => { secs[attacher(r.start)].rituels.push(r); });
    resolutions.forEach((r) => { secs[attacher(r.start)].resolutions.push(r); });
    secs.forEach((sec, i) => {
      if (i === secs.length - 1) return;
      const fin = new Date(sec.periode.date_fin).getTime();
      const debutSuivant = new Date(periodes[i + 1].date_debut);
      sec.vacancesApres = vacancesScolaires.find((v) => {
        if (!v.title || !v.title.includes('Vacances')) return false;
        const dv = new Date(v.start);
        return dv.getTime() >= fin - 24 * 3600 * 1000 && dv <= debutSuivant;
      }) || null;
    });
    // Toutes les périodes s'affichent, même vides : on doit pouvoir y
    // planifier son premier rituel (retour prof 2026-08-25).
    return secs;
  }, [railActif, periodes, sequences, rituels, resolutions, vacancesScolaires]);

  // Rubrique de chaque attendu du référentiel (objectif / automatisme) —
  // les attendus_details des séquences ne la portent pas toujours, le
  // panneau détail la résout par cet index (thèmes déjà chargés).
  const rubriqueParAttendu = useMemo(() => {
    const map = new Map();
    (Array.isArray(themes) ? themes : []).forEach((t) =>
      (t.descripteur || []).forEach((d) =>
        (d.attendus || []).forEach((att) => map.set(att.id, att.rubrique))));
    return map;
  }, [themes]);

  // ── Couverture du programme : attendus NON ABORDÉS par la progression
  // commune (arbitrages prof 2026-08-25 : seuls les ATTENDUS comptent,
  // jamais les savoir-faires ; TOUT type d'activité aborde ; manquants
  // regroupés par catégorie — partition objectifs/automatismes puis thème).
  // Référentiel = useTheme(niveau), déjà chargé : zéro appel de plus.
  // L'ensemble des attendus déjà abordés quelque part dans la progression
  // (tout type d'activité) — nourrit la couverture ET le filtre
  // « uniquement les restants » des pickers (demande prof 2026-08-25).
  const attendusAbordesIds = useMemo(() => {
    const abordes = new Set();
    (filteredSequences || []).forEach((s) => {
      (s.attendus_details || []).forEach((a) => abordes.add(a.id));
      (s.attendus || []).forEach((a) => abordes.add(typeof a === 'object' ? a.id : a));
    });
    return abordes;
  }, [filteredSequences]);

  const couverture = useMemo(() => {
    if (!Array.isArray(themes) || themes.length === 0) return null;
    const abordes = attendusAbordesIds;
    const manquants = [];
    let totAutomatismes = 0;   // le référentiel du niveau en compte-t-il ?
    themes.forEach((t) => (t.descripteur || []).forEach((d) => (d.attendus || []).forEach((att) => {
      if (att.rubrique === 'automatisme') totAutomatismes++;
      if (!abordes.has(att.id)) manquants.push({ ...att, themeNom: t.nom });
    })));
    const { objectifs, automatismes } = partitionnerParRubrique(manquants);
    const grouper = (liste) => {
      const parTheme = new Map();
      liste.forEach((att) => {
        if (!parTheme.has(att.themeNom)) parTheme.set(att.themeNom, []);
        parTheme.get(att.themeNom).push(att);
      });
      return [...parTheme.entries()].map(([theme, attendus]) => ({ theme, attendus }));
    };
    return {
      objectifs: grouper(objectifs),
      automatismes: grouper(automatismes),
      nbObjectifs: objectifs.length,
      nbAutomatismes: automatismes.length,
      // référentiel sans automatismes (le français aujourd'hui) : la partie
      // automatismes du badge et du panneau est masquée
      avecAutomatismes: totAutomatismes > 0,
    };
  }, [themes, attendusAbordesIds]);

  // Calculer les stats (simplifié - toutes les séquences ont le même design)
  const totalSequences = sequences.length;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-sky-50 via-blue-100 to-cyan-100 text-gray-800 overflow-x-hidden">
      {/* Background avec blobs liquid morphing style CardClasse */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Blobs SVG animés avec morphing - style CardClasse */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          <defs>
            {/* Gradient 1 - Cyan clair */}
            <linearGradient id="roadmap-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#67e8f9" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.2" />
            </linearGradient>

            {/* Gradient 2 - Sky blue */}
            <linearGradient id="roadmap-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#bae6fd" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.25" />
            </linearGradient>

            {/* Gradient 3 - Très clair */}
            <linearGradient id="roadmap-blob3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#bae6fd" stopOpacity="0.2" />
            </linearGradient>

            {/* Gradient 4 - Bleu cyan mix */}
            <linearGradient id="roadmap-blob4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.15" />
            </linearGradient>

            {/* Gradient 5 - Extra blob */}
            <linearGradient id="roadmap-blob5" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#cffafe" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#a5f3fc" stopOpacity="0.2" />
            </linearGradient>

            {/* Radial glow */}
            <radialGradient id="roadmap-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#e0f2fe" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Blob 1 - Grand, haut gauche */}
          <path fill="url(#roadmap-blob1)">
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="
                M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20;
                M10,15 Q25,0 35,12 Q45,24 42,42 Q39,60 22,52 Q5,44 10,15;
                M8,22 Q18,8 32,18 Q46,28 42,45 Q38,62 23,52 Q8,42 8,22;
                M5,20 Q15,5 30,15 Q45,25 40,40 Q35,55 20,50 Q5,45 5,20
              "
            />
          </path>

          {/* Blob 2 - Droite milieu */}
          <path fill="url(#roadmap-blob2)">
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10;
                M75,8 Q95,12 95,32 Q95,52 82,48 Q69,44 70,24 Q71,4 75,8;
                M68,12 Q88,18 90,38 Q92,58 78,52 Q64,46 66,26 Q68,6 68,12;
                M70,10 Q90,15 92,35 Q94,55 80,50 Q66,45 68,25 Q70,5 70,10
              "
            />
          </path>

          {/* Blob 3 - Centre bas */}
          <path fill="url(#roadmap-blob3)">
            <animate
              attributeName="d"
              dur="7s"
              repeatCount="indefinite"
              values="
                M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60;
                M38,58 Q55,52 65,65 Q75,78 58,86 Q41,94 34,80 Q27,66 38,58;
                M33,62 Q48,58 58,70 Q68,82 52,90 Q36,98 30,84 Q24,70 33,62;
                M35,60 Q50,55 60,68 Q70,81 55,88 Q40,95 32,82 Q24,69 35,60
              "
            />
          </path>

          {/* Blob 4 - Gauche bas */}
          <path fill="url(#roadmap-blob4)">
            <animate
              attributeName="d"
              dur="9s"
              repeatCount="indefinite"
              values="
                M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55;
                M0,52 Q12,46 24,55 Q36,64 30,78 Q24,92 10,86 Q-4,80 0,52;
                M0,58 Q8,54 18,62 Q28,70 22,84 Q16,98 6,90 Q-4,82 0,58;
                M0,55 Q10,50 20,58 Q30,66 25,80 Q20,94 8,88 Q-4,82 0,55
              "
            />
          </path>

          {/* Blob 5 - Haut droite */}
          <path fill="url(#roadmap-blob5)">
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0;
                M88,2 Q102,8 100,22 Q98,36 86,34 Q74,32 79,16 Q84,0 88,2;
                M83,0 Q98,4 96,18 Q94,32 82,30 Q70,28 76,12 Q82,-4 83,0;
                M85,0 Q100,5 98,20 Q96,35 85,32 Q74,29 78,14 Q82,-1 85,0
              "
            />
          </path>

          {/* Blob 6 - Centre haut */}
          <path fill="url(#roadmap-blob1)">
            <animate
              attributeName="d"
              dur="11s"
              repeatCount="indefinite"
              values="
                M45,5 Q60,0 68,12 Q76,24 65,32 Q54,40 46,28 Q38,16 45,5;
                M48,3 Q62,-2 72,10 Q82,22 70,32 Q58,42 48,30 Q38,18 48,3;
                M42,7 Q56,2 66,14 Q76,26 64,35 Q52,44 43,32 Q34,20 42,7;
                M45,5 Q60,0 68,12 Q76,24 65,32 Q54,40 46,28 Q38,16 45,5
              "
            />
          </path>

          {/* Blob 7 - Milieu droite bas */}
          <path fill="url(#roadmap-blob2)">
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="
                M80,65 Q95,60 98,75 Q101,90 88,92 Q75,94 75,80 Q75,66 80,65;
                M82,62 Q97,58 100,72 Q103,86 90,90 Q77,94 78,78 Q79,62 82,62;
                M78,68 Q93,64 96,78 Q99,92 86,95 Q73,98 74,82 Q75,66 78,68;
                M80,65 Q95,60 98,75 Q101,90 88,92 Q75,94 75,80 Q75,66 80,65
              "
            />
          </path>

          {/* Petites particules flottantes */}
          <circle cx="15" cy="40" r="1.5" fill="#67e8f9" opacity="0.6">
            <animate attributeName="cy" dur="4s" values="40;35;40" repeatCount="indefinite" />
            <animate attributeName="opacity" dur="4s" values="0.6;0.9;0.6" repeatCount="indefinite" />
          </circle>
          <circle cx="55" cy="25" r="1" fill="#7dd3fc" opacity="0.5">
            <animate attributeName="cy" dur="5s" values="25;20;25" repeatCount="indefinite" />
          </circle>
          <circle cx="75" cy="45" r="1.2" fill="#a5f3fc" opacity="0.5">
            <animate attributeName="cy" dur="3.5s" values="45;40;45" repeatCount="indefinite" />
          </circle>
          <circle cx="25" cy="75" r="1" fill="#bae6fd" opacity="0.6">
            <animate attributeName="cy" dur="4.5s" values="75;70;75" repeatCount="indefinite" />
          </circle>
          <circle cx="90" cy="30" r="0.8" fill="#67e8f9" opacity="0.4">
            <animate attributeName="cy" dur="3s" values="30;26;30" repeatCount="indefinite" />
          </circle>
          <circle cx="60" cy="85" r="1.3" fill="#7dd3fc" opacity="0.5">
            <animate attributeName="cy" dur="5.5s" values="85;80;85" repeatCount="indefinite" />
          </circle>

          {/* Glow central subtil */}
          <ellipse cx="50" cy="50" rx="35" ry="25" fill="url(#roadmap-glow)">
            <animate attributeName="rx" dur="6s" values="35;40;35" repeatCount="indefinite" />
            <animate attributeName="ry" dur="6s" values="25;30;25" repeatCount="indefinite" />
          </ellipse>
        </svg>

        {/* Grands cercles flous pour effet de profondeur - couleurs ajustées */}
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-cyan-100/40 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-40 right-1/4 w-64 h-64 bg-sky-100/50 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-10 w-56 h-56 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-10 w-48 h-48 bg-cyan-50/60 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 left-1/3 w-60 h-60 bg-sky-50/50 rounded-full blur-3xl animate-float-delayed" />

        {/* Grid pattern subtil */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Header — max-w aligné sur le main (élargi pour les DEUX rails,
          automatismes à gauche + résolutions à droite) */}
      <header className="relative z-20 pt-4 sm:pt-6 md:pt-10 xl:pt-6 2xl:pt-8 3xl:pt-10 pb-4 sm:pb-6 xl:pb-4 2xl:pb-5 3xl:pb-6 px-3 sm:px-4 md:px-8 xl:px-6 2xl:px-7 3xl:px-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Top bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 overflow-visible">
            {/* Titre */}
            <div className="flex items-center gap-2 sm:gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 bg-white/70 backdrop-blur-md rounded-lg sm:rounded-xl flex items-center justify-center border border-sky-200 shadow-lg flex-shrink-0">
                <GraduationCap className="w-5 h-5 sm:w-7 sm:h-7 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl md:text-2xl xl:text-base 2xl:text-lg 3xl:text-2xl font-bold text-gray-800 flex items-center gap-1 sm:gap-2 flex-wrap">
                  <span className="truncate">Programmation Pédagogique</span>
                  <Sparkles className="w-4 h-4 sm:w-6 sm:h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-amber-500 animate-pulse flex-shrink-0" />
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm hidden sm:block">Gérez et organisez votre programmation annuelle</p>
              </div>
            </div>

            {/* Sélection niveau + Bouton Import */}
            <div className="flex items-center gap-2 sm:gap-4 overflow-visible">
              {/* Dropdown sélection niveau */}
              <div className="relative overflow-visible z-20 flex-1 sm:flex-none">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`
                    w-full sm:min-w-[160px] md:min-w-[180px] xl:min-w-[140px] 2xl:min-w-[160px] 3xl:min-w-[180px] px-3 sm:px-4 md:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2 sm:py-2.5 md:py-3 xl:py-2 2xl:py-2.5 3xl:py-3
                    ${selectedNiveau
                      ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white'
                      : 'bg-white/90 text-gray-700'
                    }
                    rounded-xl sm:rounded-2xl border border-sky-200
                    font-semibold
                    shadow-lg hover:shadow-xl
                    transition-all duration-300
                    flex items-center justify-between gap-2 sm:gap-3
                    hover:scale-[1.02] sm:hover:scale-105 focus:outline-none
                  `}
                >
                  <span className="flex items-center text-sm sm:text-base md:text-lg xl:text-sm 2xl:text-sm 3xl:text-lg gap-1.5 sm:gap-2">
                    {selectedNiveau ? (
                      <>
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="truncate">
                          {niveaux.find(n => n.value === selectedNiveau)?.label || 'Niveau'}
                        </span>
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                        <span className="truncate">Choisir niveau</span>
                      </>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Liste déroulante */}
                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 w-full bg-white
                   rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[100]">
                    {niveaux.map((niveau) => (
                      <button
                        key={niveau.value}
                        onClick={() => handleNiveauSelect(niveau)}
                        className={`
                          w-full px-3 sm:px-4 py-2.5 sm:py-3
                          text-sm sm:text-base md:text-lg xl:text-sm 2xl:text-sm 3xl:text-lg font-medium
                          hover:bg-gradient-to-r hover:from-blue-400 hover:to-cyan-400 hover:text-white
                          transition-all duration-200
                          ${selectedNiveau === niveau.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}
                        `}
                      >
                        {niveau.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bouton Importer - visible seulement si niveau sélectionné */}
              {selectedNiveau && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2 sm:py-2.5 md:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-white/80 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200
                           hover:bg-white transition-all duration-300 hover:scale-[1.02] sm:hover:scale-105 group flex-shrink-0 shadow-lg"
                >
                  <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 group-hover:text-blue-700 transition-colors" />
                  <span className="font-semibold text-sm sm:text-base md:text-lg xl:text-sm 2xl:text-sm 3xl:text-lg text-gray-700 hidden xs:inline">Importer</span>
                </button>
              )}
            </div>
          </div>

          {/* Stats rapides - visible seulement si niveau sélectionné */}
          {selectedNiveau && (
            <>
              {/* Info séquences */}
              <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                    <div className="p-2 xl:p-1.5 2xl:p-2 3xl:p-2 bg-gradient-to-br from-blue-500 to-sky-500 rounded-lg sm:rounded-xl shadow-md">
                      <Target className="w-5 h-5 sm:w-6 sm:h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-600">
                        Nombre total de séquences à traiter cette année scolaire pour le niveau
                      </p>
                      <p className="text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-800">
                        {niveaux.find(n => n.value === selectedNiveau)?.label || selectedNiveau}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl sm:text-4xl md:text-5xl xl:text-3xl 2xl:text-4xl 3xl:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500">
                      {totalSequences}
                    </span>
                    <span className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-500 font-medium">
                      séquence{totalSequences > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              {/* Légende activités */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 xl:gap-3 2xl:gap-4 3xl:gap-6 mt-3 sm:mt-4 md:mt-6 xl:mt-3 2xl:mt-4 3xl:mt-6 justify-center">
                <span className="text-[10px] sm:text-xs text-gray-600 uppercase tracking-wider font-semibold hidden sm:inline">Activités :</span>
                {user?.matiere !== 'Français' && (
                  <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-emerald-800 font-bold bg-emerald-100 px-2 py-1 rounded-lg">
                    <span>⚡</span> <span className="hidden xs:inline">Automatismes</span><span className="xs:hidden">Auto.</span>
                  </div>
                )}
                {user?.matiere !== 'Français' && (
                  <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-rose-800 font-bold bg-rose-100 px-2 py-1 rounded-lg">
                    <span>🧩</span> <span className="hidden xs:inline">Résolutions</span><span className="xs:hidden">Résol.</span>
                  </div>
                )}
                <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-amber-800 font-bold bg-amber-100 px-2 py-1 rounded-lg">
                  <span>💡</span> QFlash
                </div>
              </div>
            </>
          )}

          {/* Message d'accueil si aucun niveau sélectionné */}
          {!selectedNiveau && (
            <div className="mt-4 sm:mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6 p-4 sm:p-6 xl:p-3 2xl:p-4 3xl:p-6 bg-white/70 backdrop-blur-md rounded-xl sm:rounded-2xl border border-sky-200 shadow-lg">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 text-center sm:text-left">
                <div className="p-2.5 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-blue-100 rounded-lg sm:rounded-xl flex-shrink-0">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-8 3xl:h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-bold text-gray-800 mb-1">Bienvenue sur votre espace de programmation</h3>
                  <p className="text-gray-600 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                    Sélectionnez un niveau ci-dessus pour consulter, créer ou éditer la programmation pédagogique commune.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Timeline - visible seulement si niveau sélectionné */}
      {selectedNiveau && (
        <main className="relative z-10 px-3 sm:px-4 md:px-8 xl:px-6 2xl:px-7 3xl:px-8 pb-10 sm:pb-16 md:pb-20 xl:pb-12 2xl:pb-16 3xl:pb-20">
          <div className="max-w-[1400px] mx-auto">
            {/* Badge couverture : attendus non abordés par la progression */}
            {couverture && (couverture.nbObjectifs > 0 || couverture.nbAutomatismes > 0) && (
              <div className="flex justify-center mb-2">
                <button
                  onClick={() => setNonAbordesOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border-2 border-amber-300 text-amber-800 text-xs sm:text-[13px] font-bold hover:bg-amber-100 hover:border-amber-400 hover:shadow-md transition-all"
                >
                  <AlertTriangle size={14} className="text-amber-500" />
                  Non abordés : {couverture.nbObjectifs} objectif{couverture.nbObjectifs > 1 ? 's' : ''}
                  {couverture.avecAutomatismes ? ` · ${couverture.nbAutomatismes} automatisme${couverture.nbAutomatismes > 1 ? 's' : ''}` : ''}
                </button>
              </div>
            )}

            {/* Bouton Ajouter une séquence */}
            <ButtonAddSequenceNiveau onClick={handleOpenAddSequence} />

            {/* Séquences */}
            {sequences.length > 0 ? (
              sections ? (
                /* ── Frise par PÉRIODES : rail des rituels (desktop) /
                   bandeau (mobile) + séquences, vacances entre sections ── */
                <div className="py-6 sm:py-8 md:py-12 xl:py-6 2xl:py-8 3xl:py-12">
                  {sections.map((sec) => {
                    const ajouterRituel = () => handleOpenAddRituel(sec.periode);
                    const ajouterResolution = () => handleOpenAddResolution(sec.periode);
                    return (
                      <React.Fragment key={`periode-${sec.periode.numero}`}>
                        {/* 3 colonnes dès lg : rail automatismes | frise |
                            rail résolutions — rails resserrés à 200px sous
                            xl pour laisser vivre les cartes */}
                        <div className="lg:grid lg:gap-4 lg:grid-cols-[200px_minmax(0,1fr)_200px] xl:gap-5 xl:grid-cols-[225px_minmax(0,1fr)_225px]">
                          {/* Rail desktop gauche : rituels d'automatismes */}
                          <div className="hidden lg:block">
                            <RailAutomatismes
                              periode={sec.periode}
                              rituels={sec.rituels}
                              onOpen={setContenusActifs}
                              onAdd={ajouterRituel}
                              onDelete={handleOpenDeleteSequence}
                            />
                          </div>
                          <div>
                            {/* Bandeaux mobiles : automatismes puis résolutions */}
                            <div className="lg:hidden">
                              <BandeauAutomatismes
                                periode={sec.periode}
                                rituels={sec.rituels}
                                onOpen={setContenusActifs}
                                onAdd={ajouterRituel}
                                onDelete={handleOpenDeleteSequence}
                              />
                              <BandeauAutomatismes
                                type="RESOLUTION"
                                periode={sec.periode}
                                rituels={sec.resolutions}
                                onOpen={setContenusActifs}
                                onAdd={ajouterResolution}
                                onDelete={handleOpenDeleteSequence}
                              />
                            </div>
                            <div className="relative space-y-12 sm:space-y-16 md:space-y-24 xl:space-y-14 2xl:space-y-18 3xl:space-y-24">
                              {/* Ligne verticale de la section */}
                              <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 sm:w-1 -translate-x-1/2">
                                <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-sky-400/40 to-cyan-300/20 rounded-full" />
                              </div>
                              {sec.sequences.map(({ sequence, index }) => (
                                <SequenceNode
                                  key={sequence.id}
                                  sequence={sequence}
                                  index={index}
                                  activitesParalleles={activitesParalleles[sequence.id]}
                                  onAddSavoir={handleOpenAddSavoir}
                                  onEdit={handleOpenEditSequence}
                                  onDelete={handleOpenDeleteSequence}
                                  onAddActivity={handleOpenAddActivity}
                                  onViewActivities={handleViewActivities}
                                  onViewContenus={setContenusActifs}
                                />
                              ))}
                            </div>
                          </div>
                          {/* Rail desktop droit : résolutions de problèmes */}
                          <div className="hidden lg:block">
                            <RailAutomatismes
                              type="RESOLUTION"
                              periode={sec.periode}
                              rituels={sec.resolutions}
                              onOpen={setContenusActifs}
                              onAdd={ajouterResolution}
                              onDelete={handleOpenDeleteSequence}
                            />
                          </div>
                        </div>
                        {sec.vacancesApres ? (
                          <div className="my-8 sm:my-10">
                            <VacancesSeparator
                              title={sec.vacancesApres.title}
                              start={sec.vacancesApres.start}
                              end={sec.vacancesApres.end}
                            />
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                /* ── Repli historique (périodes indisponibles) : frise à plat,
                   vacances insérées par la règle d'intervalle ── */
                <div className="relative space-y-12 sm:space-y-16 md:space-y-24 xl:space-y-14 2xl:space-y-18 3xl:space-y-24 py-6 sm:py-8 md:py-12 xl:py-6 2xl:py-8 3xl:py-12">
                  <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 sm:w-1 -translate-x-1/2">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-sky-400/40 to-cyan-300/20 rounded-full" />
                  </div>
                  {timeline.map((item) => (
                    item.type === 'vacances' ? (
                      <VacancesSeparator
                        key={`${item.title}-${item.start}`}
                        title={item.title}
                        start={item.start}
                        end={item.end}
                      />
                    ) : (
                      <SequenceNode
                        key={item.sequence.id}
                        sequence={item.sequence}
                        index={item.index}
                        activitesParalleles={activitesParalleles[item.sequence.id]}
                        onAddSavoir={handleOpenAddSavoir}
                        onEdit={handleOpenEditSequence}
                        onDelete={handleOpenDeleteSequence}
                        onAddActivity={handleOpenAddActivity}
                        onViewActivities={handleViewActivities}
                        onViewContenus={setContenusActifs}
                      />
                    )
                  ))}
                </div>
              )
            ) : (!sequencesInitialisees || isLoading) ? (
              /* Chargement en cours : ne JAMAIS conclure « Aucune séquence »
                 tant que la requête sequences/ n'a pas répondu (connexion
                 lente = faux vide qui prescrivait un ré-import) */
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 xl:py-12 2xl:py-16 3xl:py-20">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20 3xl:w-24 3xl:h-24 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg border border-sky-200">
                  <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 text-sky-500 animate-spin" />
                </div>
                <p className="text-gray-600 text-center max-w-md text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base px-4">
                  Chargement de la programmation…
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 xl:py-12 2xl:py-16 3xl:py-20">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20 3xl:w-24 3xl:h-24 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg border border-sky-200">
                  <span className="text-2xl sm:text-3xl md:text-4xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl">📚</span>
                </div>
                <h3 className="text-lg sm:text-xl xl:text-base 2xl:text-lg 3xl:text-xl font-bold text-gray-800 mb-2">Aucune séquence</h3>
                <p className="text-gray-600 text-center max-w-md text-sm sm:text-base xl:text-xs 2xl:text-sm 3xl:text-base px-4">
                  Il n'y a pas encore de séquence créée pour ce niveau.
                  Utilisez le bouton Importer pour ajouter votre progression.
                </p>
              </div>
            )}

            {/* Fin d'année marker */}
            {sequences.length > 0 && (
              <div className="relative flex justify-center mt-8 sm:mt-10 md:mt-12 xl:mt-8 2xl:mt-10 3xl:mt-12">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-2xl shadow-emerald-500/40 border-2 sm:border-4 border-white/50">
                  <span className="text-xl sm:text-2xl">🎓</span>
                </div>
              </div>
            )}

            {/* Filet d'affichage : activités dont la date de début ne tombe
                dans aucune séquence (séquence éditée/supprimée après coup).
                Sans ce bloc elles restaient enregistrées mais invisibles. */}
            {activitesOrphelines.length > 0 && (
              <div className="mt-8 sm:mt-10 bg-white/80 backdrop-blur-md rounded-2xl border border-amber-300 shadow-lg p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-bold text-gray-800">
                    Activités hors séquence ({activitesOrphelines.length})
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mb-3">
                  Leur date de début ne tombe dans aucune séquence de la frise
                  (séquence modifiée ou supprimée depuis). Elles restent
                  enregistrées : supprimez-les ou recréez-les à une date couverte.
                </p>
                <ul className="space-y-1.5">
                  {activitesOrphelines.map((act) => (
                    <li key={act.id} className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                      <span className="font-semibold">{act.title}</span>
                      <span className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[10px] sm:text-xs text-amber-700">{act.type}</span>
                      <span className="text-gray-500">
                        {new Date(act.start).toLocaleDateString('fr-FR')}
                        {act.end && act.end !== act.start ? ` au ${new Date(act.end).toLocaleDateString('fr-FR')}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(20px) scale(1.02); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }
      `}</style>

      {/* Modal Import */}
      {isModalOpen && (
        <ImportProgressionNiveau
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          etablissement={etablissement}
          selectedNiveau={selectedNiveau}
          handleReload={handleReload}
          filteredSequences={filteredSequences}
        />
      )}

      {/* Modal Ajout Savoirs */}
      <AddSavoirToSequence
        dejaAbordes={attendusAbordesIds}
        isOpen={isAddSavoirModalOpen}
        onClose={handleCloseAddSavoir}
        sequence={selectedSequenceForSavoir}
        theme={getThemeForSequence(selectedSequenceForSavoir)}
        onSave={handleSaveSavoirs}
        isLoading={isSavingSavoirs}
      />

      {/* Modal Édition Séquence */}
      <EditSequenceNiveau
        isOpen={isEditModalOpen}
        onClose={handleCloseEditSequence}
        sequence={selectedSequenceForEdit}
        themes={themes}
        onSave={handleSaveEditSequence}
        isLoading={isEditingSequence}
      />

      {/* Modal Création Séquence */}
      <AddSequenceNiveau
        isOpen={isAddSequenceModalOpen}
        onClose={handleCloseAddSequence}
        themes={themes}
        niveau={selectedNiveau}
        onSave={handleCreateSequence}
        isLoading={isCreatingSequence}
        dejaAbordes={attendusAbordesIds}
      />

      {/* Modal Confirmation Suppression */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteSequence}
        onConfirm={handleConfirmDeleteSequence}
        sequence={selectedSequenceForDelete}
        isDeleting={isDeleting}
      />

      {/* Modal Ajout Activité en Parallèle */}
      <AddNiveauActivityProgrammation
        isOpen={isAddActivityModalOpen}
        onClose={handleCloseAddActivity}
        sequence={selectedSequenceForActivity}
        themes={themes}
        onSave={handleCreateActivity}
        isLoading={isCreatingActivity}
        matiere={user?.matiere}
        typeInitial={activityTypeInitial}
        dejaAbordes={attendusAbordesIds}
      />

      {/* Modal Liste des Activités */}
      <ActivityListModal
        isOpen={isActivityListModalOpen}
        onClose={handleCloseActivityList}
        type={activityListModalData.type}
        activities={activityListModalData.activities}
        sequenceTitle={activityListModalData.sequenceTitle}
        onDeleteActivity={handleOpenDeleteSequence}
      />

      {/* Panneau détail partagé (pill séquence / chip rituel) */}
      <PanneauContenus
        isOpen={contenusActifs !== null}
        activite={contenusActifs}
        rubriques={rubriqueParAttendu}
        onClose={() => setContenusActifs(null)}
        onAddSavoir={handleOpenAddSavoir}
      />

      {/* Panneau des attendus non abordés */}
      <PanneauNonAbordes
        isOpen={nonAbordesOpen}
        onClose={() => setNonAbordesOpen(false)}
        couverture={couverture}
      />
    </div>
  );
}

export default RoadMapNiveau;
